import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { tokens } from "../../theme";
import { foyerApi, type OwnerShell } from "../../http/foyer";
import { PageHeader } from "../../components/PageHeader";
import type { ContentDeal } from "@shared/content-deal";
import type { ContentStatement } from "@shared/content-statement";

/**
 * Revenue — what a supplier earned, and what is still owed (`WL-6`).
 *
 * ## Two questions, and they are not the same question
 *
 * `GET /owner/statement` answers *what did I earn in a period* — recognised
 * money, from deals that reached PAID and from storefront orders, with Nenda's
 * share separated out. `GET /owner/deals` answers *where is each piece of
 * business* — draft, offered, accepted, invoiced, paid. A page that showed only
 * the first would be a supplier's accounts; a page that showed only the second
 * would be their pipeline. They want both, side by side, and the gap between
 * them is exactly the conversation they want to have with us.
 *
 * ## ⚠️ The default period is ONE MONTH, and it says so
 *
 * With no dates the server answers for the **last full calendar month**, not
 * for all time. A screen that printed that figure under a bare "Revenue"
 * heading would be reporting one month as the lifetime total, and nobody would
 * catch it because the number is plausible. The period is on the page, in
 * words, and it is editable.
 *
 * ## `revenueShareBps` is null until it is agreed
 *
 * Not zero. A statement whose share is unagreed shows the gross and refuses to
 * invent a split — printing "Nenda's share: 0" would be a commitment neither
 * side made.
 *
 * ## Overdue is asked for separately
 *
 * `GET /owner/deals/overdue` applies a grace period the client does not model,
 * so the answer comes from the server rather than from filtering the deal list
 * by a date this page guessed at.
 */

interface ShellContext {
  shell: OwnerShell;
}

const STATE_LABEL: Record<string, string> = {
  draft: "Draft",
  offered: "Offered",
  accepted: "Accepted",
  invoiced: "Invoiced",
  paid: "Paid",
  cancelled: "Cancelled",
};

/** Money as a partner reads it, not as it is stored. */
function money(amount: number, currency: string): string {
  return `${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`;
}

/** An ISO instant as a date input wants it. */
function dayOf(iso: string): string {
  return iso.slice(0, 10);
}

export default function SupplierRevenuePage() {
  useOutletContext<ShellContext>();
  const [statement, setStatement] = useState<ContentStatement | null>(null);
  const [deals, setDeals] = useState<ContentDeal[] | null>(null);
  const [overdue, setOverdue] = useState<ContentDeal[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);

  const load = useCallback(async (asked?: { from: string; to: string }) => {
    try {
      const [s, d, o] = await Promise.all([
        foyerApi.ownerStatement(asked),
        foyerApi.ownerDeals(),
        // Its own call: the grace period is the server's rule, and filtering
        // the deal list here would be this page inventing a second one.
        foyerApi.ownerOverdueDeals(),
      ]);
      setStatement(s);
      setDeals(d);
      setOverdue(o);
      // The server decides the default window; echo back what it used rather
      // than what we asked for.
      setRange({ from: dayOf(s.from), to: dayOf(s.to) });
      setError(null);
    } catch (e) {
      const err = e as { code?: string; message: string };
      setError(
        err.code === "invalid_period"
          ? "That period does not make sense — the end has to come after the start."
          : err.message
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!statement || !deals)
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress size={26} />
      </Box>
    );

  const panel = {
    background: tokens.surface,
    border: `1px solid ${tokens.divider}`,
    borderRadius: 2,
    p: 2.5,
  };

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow="Your account"
        title="Revenue"
        subtitle="What your material earned, and where each piece of business stands."
      />

      {overdue.length > 0 ? (
        <Alert severity="warning" data-testid="revenue-overdue">
          {overdue.length} invoice{overdue.length === 1 ? "" : "s"} are past
          due. We chase these — you do not have to.
        </Alert>
      ) : null}

      <Box sx={panel}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "flex-end" }}
          sx={{ mb: 2 }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>Statement</Typography>
            {/* 🚨 The period, in words. With no dates the server answers for
                the last full calendar month — a figure that reads exactly like
                a lifetime total if nobody says otherwise. */}
            <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
              {new Date(statement.from).toLocaleDateString()} to{" "}
              {new Date(statement.to).toLocaleDateString()}
            </Typography>
          </Box>
          <TextField
            size="small"
            type="date"
            label="From"
            value={range?.from ?? ""}
            onChange={(e) =>
              setRange((r) => ({ from: e.target.value, to: r?.to ?? "" }))
            }
            InputLabelProps={{ shrink: true }}
            inputProps={{ "data-testid": "statement-from" }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={range?.to ?? ""}
            onChange={(e) =>
              setRange((r) => ({ from: r?.from ?? "", to: e.target.value }))
            }
            onBlur={() => range && void load(range)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ "data-testid": "statement-to" }}
          />
        </Stack>

        {statement.totals.length === 0 ? (
          <Typography sx={{ color: tokens.inkMuted }}>
            Nothing was earned in this period. Money appears here when an
            invoice is paid, or when somebody buys a title from your storefront.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {statement.totals.map((total) => (
              <Stack
                key={total.currency}
                direction="row"
                flexWrap="wrap"
                spacing={3}
                data-testid={`total-${total.currency}`}
              >
                <Figure
                  label="Gross"
                  value={money(total.gross, total.currency)}
                />
                {total.reversed > 0 ? (
                  <Figure
                    label="Refunded"
                    value={money(-total.reversed, total.currency)}
                  />
                ) : null}
                <Figure
                  label="Net"
                  value={money(total.net, total.currency)}
                  strong
                />
                {/* Unagreed is not zero. A split neither side has agreed is
                    not a number this page may invent. */}
                {total.ownerShare === null ? (
                  <Figure label="Your share" value="Not agreed yet" />
                ) : (
                  <>
                    <Figure
                      label="Your share"
                      value={money(total.ownerShare, total.currency)}
                      strong
                    />
                    <Figure
                      label="Nenda's share"
                      value={money(total.nendaShare ?? 0, total.currency)}
                    />
                  </>
                )}
              </Stack>
            ))}
          </Stack>
        )}

        {statement.unreconciled > 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            {statement.unreconciled} order
            {statement.unreconciled === 1 ? " has" : "s have"} no payment
            reference yet, so{" "}
            {statement.unreconciled === 1 ? "it is" : "they are"} counted here
            but not yet reconciled.
          </Alert>
        ) : null}
      </Box>

      <Box sx={panel}>
        <Typography sx={{ fontWeight: 600, mb: 1.5 }}>Your deals</Typography>
        {deals.length === 0 ? (
          <Typography sx={{ color: tokens.inkMuted }}>
            No deals yet. When Nenda agrees terms with a school, library or
            other customer for your material, each one appears here with its
            state.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    Customer
                  </TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>Titles</TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>Price</TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>State</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deals.map((deal) => (
                  <TableRow key={deal.id} data-testid={`deal-${deal.id}`}>
                    <TableCell>
                      {deal.customerName}
                      <Typography
                        variant="caption"
                        sx={{ color: tokens.inkFaint, display: "block" }}
                      >
                        {deal.customerKind} · {deal.customerCountry}
                      </Typography>
                    </TableCell>
                    <TableCell>{deal.assetIds.length}</TableCell>
                    <TableCell>
                      {money(deal.price.amount, deal.price.currency)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={STATE_LABEL[deal.state] ?? deal.state}
                        color={deal.state === "paid" ? "primary" : "default"}
                        variant={deal.state === "paid" ? "filled" : "outlined"}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Stack>
  );
}

function Figure({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: tokens.inkFaint }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: strong ? 22 : 18,
          color: tokens.ink,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}
